var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { faBanParking, faCircleParking } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableFooter, TableRow, Tooltip, Typography, styled } from '@mui/material';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TABLE_HIERARCHY_COLORS } from '../../../enum';
import { formatNumberByLocale } from '../../../util';
import { calcTotalShrinkage, daysFromHours, hoursFromDays, hoursFromPercent, hoursPerMonthFromHoursPerYear, hoursPerWeekFromHoursPerYear, percentFromHours, round2 } from '../../../util/shrinkageCalculations';
import { useTerminologyTranslation } from '../../app/terminology';
import { NativeNumberInput } from '../../common/nativeNumberInput';
import { TABLE_HIERARCHY_INDENTS } from '../../common/tableHierarchy';
import { SHRINKAGE_SUBCATEGORY_TRANS_KEYS, ShrinkageSubcategory } from '../../wizard/shrinkage/constants';
import { getWizardTableSx } from '../common/WizardTable';
import { getFactorName, getSubcategoryLabel } from './ShrinkageStep';
import { SHRINKAGE_CATEGORY_IN_OFFICE, SHRINKAGE_CATEGORY_OUT_OF_OFFICE, SHRINKAGE_FACTOR_I18N_KEYS } from './shrinkageConstants';
var roundModalValue = function (value) {
  return round2(value);
};
var SHRINKAGE_INPUT_DECIMALS = 2;
export var TOTAL_DAYS_LIMIT = 365;
export var TOTAL_HOURS_LIMIT = 2920;
export var TOTAL_PCT_LIMIT = 99.99;
var StyledDialogContent = styled(DialogContent)({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  minHeight: '50vh',
  maxHeight: '80vh',
  overflowY: 'auto'
});
function buildFactorGroups(sectionRows) {
  var order = [];
  var map = new Map();
  for (var _i = 0, sectionRows_1 = sectionRows; _i < sectionRows_1.length; _i++) {
    var row = sectionRows_1[_i];
    var name_1 = getFactorName(row);
    var group = map.get(name_1);
    if (!group) {
      group = [];
      map.set(name_1, group);
      order.push(name_1);
    }
    group.push(row);
  }
  return order.map(function (factorName) {
    var _a;
    return {
      factorName: factorName,
      rows: (_a = map.get(factorName)) !== null && _a !== void 0 ? _a : []
    };
  });
}
var numericDisplaySx = {
  fontFeatureSettings: '"tnum"',
  fontVariantNumeric: 'tabular-nums'
};
function ShrinkageNumericCellInput(_a) {
  var value = _a.value,
    onValueChange = _a.onValueChange,
    ariaLabel = _a.ariaLabel,
    max = _a.max,
    disabled = _a.disabled;
  return _jsx(NativeNumberInput, {
    "aria-label": ariaLabel,
    value: value,
    onChange: onValueChange,
    min: 0,
    max: max,
    decimal: SHRINKAGE_INPUT_DECIMALS,
    fixedDecimalScale: true,
    disabled: disabled,
    sx: function (theme) {
      return __assign({
        fontSize: theme.typography.body2.fontSize
      }, numericDisplaySx);
    }
  });
}
var shrinkageIconWrapSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  lineHeight: 1,
  fontSize: '0.8125rem',
  '& svg': {
    display: 'block'
  }
};
export function PlannedBadge(_a) {
  var ariaLabel = _a.ariaLabel;
  return _jsx(Box, __assign({
    component: "span",
    sx: __assign(__assign({}, shrinkageIconWrapSx), {
      color: 'primary.dark'
    }),
    "aria-label": ariaLabel,
    role: "img"
  }, {
    children: _jsx(FontAwesomeIcon, {
      icon: faCircleParking
    })
  }));
}
export function UnplannedBadge(_a) {
  var ariaLabel = _a.ariaLabel;
  return _jsx(Box, __assign({
    component: "span",
    sx: __assign(__assign({}, shrinkageIconWrapSx), {
      color: 'primary.dark'
    }),
    "aria-label": ariaLabel,
    role: "img"
  }, {
    children: _jsx(FontAwesomeIcon, {
      icon: faBanParking
    })
  }));
}
function FactorLabel(_a) {
  var row = _a.row,
    label = _a.label,
    plannedAriaLabel = _a.plannedAriaLabel,
    unplannedAriaLabel = _a.unplannedAriaLabel;
  var showBadges = row.isPlanned || row.isUnplanned;
  return _jsxs(Stack, __assign({
    direction: "row",
    alignItems: "center",
    spacing: 0.75,
    sx: {
      width: '100%'
    }
  }, {
    children: [_jsx(Typography, __assign({
      variant: "body2",
      component: "span",
      sx: {
        flex: 1,
        minWidth: 0
      }
    }, {
      children: label
    })), showBadges ? _jsxs(Stack, __assign({
      direction: "row",
      alignItems: "center",
      spacing: 0.75,
      sx: {
        flexShrink: 0,
        ml: 'auto'
      }
    }, {
      children: [row.isPlanned ? _jsx(PlannedBadge, {
        ariaLabel: plannedAriaLabel
      }) : null, row.isUnplanned ? _jsx(UnplannedBadge, {
        ariaLabel: unplannedAriaLabel
      }) : null]
    })) : null]
  }));
}
var HeaderCell = function (_a) {
  var children = _a.children,
    sx = _a.sx,
    props = __rest(_a, ["children", "sx"]);
  return _jsx(TableCell, __assign({
    sx: __assign(__assign(__assign({
      bgcolor: 'text.primary',
      color: 'common.white'
    }, numericDisplaySx), {
      textAlign: 'center'
    }), sx)
  }, props, {
    children: _jsx(Typography, __assign({
      variant: "body2",
      fontWeight: 600,
      textAlign: "inherit"
    }, {
      children: children
    }))
  }));
};
export var AddShrinkageModal = function (_a) {
  var open = _a.open,
    onClose = _a.onClose,
    locationName = _a.locationName,
    paidHoursPerDay = _a.paidHoursPerDay,
    workingDaysPerYear = _a.workingDaysPerYear,
    factors = _a.factors,
    onSave = _a.onSave;
  var t = useTranslation('orgConfig', {
    keyPrefix: 'orgConfigWizard.addShrinkageModal'
  }).t;
  var tAction = useTranslation('translation', {
    keyPrefix: 'action'
  }).t;
  var tShrinkage = useTranslation('orgConfig', {
    keyPrefix: 'orgConfigWizard.shrinkageStep'
  }).t;
  var tTerm = useTerminologyTranslation(['wizard', 'translation']).t;
  var paidHoursPerYear = paidHoursPerDay * workingDaysPerYear;
  var _b = useState([]),
    rows = _b[0],
    setRows = _b[1];
  var tableSx = useMemo(function () {
    return function (theme) {
      var _a;
      return __assign(__assign({}, getWizardTableSx(theme)), (_a = {
        tableLayout: 'fixed',
        width: '100%'
      }, _a["& td:first-of-type, & th:first-of-type"] = {
        borderLeft: "1px solid",
        borderLeftColor: 'grey.600'
      }, _a["& th:last-child, & td:last-child"] = {
        borderRight: "1px solid",
        borderRightColor: 'grey.600'
      }, _a["& tr:first-of-type th, & tr:first-of-type td"] = {
        borderTop: "1px solid",
        borderTopColor: 'grey.600'
      }, _a["& tbody tr:last-of-type td"] = {
        borderBottom: "1px solid",
        borderBottomColor: 'grey.600'
      }, _a["& td:not(:first-of-type), & th:not(:first-of-type)"] = {
        textAlign: 'center'
      }, _a["& td:not(:first-of-type) input"] = {
        textAlign: 'center'
      }, _a));
    };
  }, []);
  var pctColumnBorderSx = useMemo(function () {
    return {
      borderRight: '2px solid',
      borderRightColor: 'primary.main'
    };
  }, []);
  var numericCellSx = useMemo(function () {
    return {
      textAlign: 'center',
      verticalAlign: 'middle'
    };
  }, []);
  var labelCellSx = useMemo(function () {
    return {
      verticalAlign: 'middle'
    };
  }, []);
  var sectionTotalRowSx = useMemo(function () {
    return {
      bgcolor: TABLE_HIERARCHY_COLORS.lightBlue
    };
  }, []);
  var subcategoryRowSx = useMemo(function () {
    return {
      bgcolor: TABLE_HIERARCHY_COLORS.lightGray
    };
  }, []);
  var factorRowSx = useMemo(function () {
    return {
      bgcolor: TABLE_HIERARCHY_COLORS.lightBlueGray
    };
  }, []);
  var finalTotalRowSx = useMemo(function () {
    return {
      bgcolor: 'text.primary',
      color: 'common.white',
      fontWeight: 'bold'
    };
  }, []);
  useEffect(function () {
    if (open && factors.length > 0) {
      setRows(factors.map(function (f) {
        var _a, _b, _c;
        var pct = (_a = f.percentShrinkage) !== null && _a !== void 0 ? _a : undefined;
        var hours = pct === undefined ? (_b = f.hoursPerYear) !== null && _b !== void 0 ? _b : undefined : roundModalValue(hoursFromPercent(pct, paidHoursPerYear));
        var days = hours === undefined ? (_c = f.daysPerYear) !== null && _c !== void 0 ? _c : undefined : roundModalValue(daysFromHours(hours, paidHoursPerDay));
        return __assign(__assign({}, f), {
          _days: days,
          _hours: hours,
          _pct: pct
        });
      }));
    } else {
      setRows([]);
    }
  }, [open, factors, paidHoursPerDay, paidHoursPerYear]);
  var persistNumeric = function (v) {
    return v !== undefined && Number.isFinite(v) ? v : null;
  };
  var getFieldMax = useCallback(function (field) {
    if (field === '_days') return workingDaysPerYear;
    if (field === '_hours') return paidHoursPerYear;
    return 100;
  }, [paidHoursPerYear, workingDaysPerYear]);
  var updateCell = useCallback(function (index, field, value) {
    var num = value === null || value === '' ? 0 : Number(value);
    setRows(function (prev) {
      var next = __spreadArray([], prev, true);
      var row = __assign({}, next[index]);
      next[index] = row;
      if (field === '_days') {
        row._days = num;
        row._hours = roundModalValue(hoursFromDays(num, paidHoursPerDay));
        row._pct = roundModalValue(percentFromHours(row._hours, paidHoursPerYear));
      } else if (field === '_hours') {
        row._hours = num;
        row._days = roundModalValue(daysFromHours(num, paidHoursPerDay));
        row._pct = roundModalValue(percentFromHours(num, paidHoursPerYear));
      } else {
        row._pct = num;
        row._hours = roundModalValue(hoursFromPercent(num, paidHoursPerYear));
        row._days = roundModalValue(daysFromHours(row._hours, paidHoursPerDay));
      }
      row.daysPerYear = persistNumeric(row._days);
      row.hoursPerYear = persistNumeric(row._hours);
      row.percentShrinkage = persistNumeric(row._pct);
      return next;
    });
  }, [paidHoursPerDay, paidHoursPerYear]);
  var outOfOfficeRows = rows.filter(function (r) {
    return r.shrinkageCategory === SHRINKAGE_CATEGORY_OUT_OF_OFFICE;
  });
  var inOfficeRows = rows.filter(function (r) {
    return r.shrinkageCategory === SHRINKAGE_CATEGORY_IN_OFFICE;
  });
  var oosTotal = round2(outOfOfficeRows.reduce(function (s, r) {
    var _a;
    return s + ((_a = r._pct) !== null && _a !== void 0 ? _a : 0);
  }, 0));
  var iosTotal = round2(inOfficeRows.reduce(function (s, r) {
    var _a;
    return s + ((_a = r._pct) !== null && _a !== void 0 ? _a : 0);
  }, 0));
  var totalShrinkage = round2(calcTotalShrinkage(oosTotal, iosTotal));
  var oosHours = outOfOfficeRows.reduce(function (s, r) {
    var _a;
    return s + ((_a = r._hours) !== null && _a !== void 0 ? _a : 0);
  }, 0);
  var iosHours = inOfficeRows.reduce(function (s, r) {
    var _a;
    return s + ((_a = r._hours) !== null && _a !== void 0 ? _a : 0);
  }, 0);
  var totalHours = totalShrinkage / 100 * paidHoursPerYear;
  var totalDays = totalShrinkage / 100 * workingDaysPerYear;
  var oosDays = outOfOfficeRows.reduce(function (s, r) {
    var _a;
    return s + ((_a = r._days) !== null && _a !== void 0 ? _a : 0);
  }, 0);
  var iosDays = inOfficeRows.reduce(function (s, r) {
    var _a;
    return s + ((_a = r._days) !== null && _a !== void 0 ? _a : 0);
  }, 0);
  var pctOverLimitRows = rows.filter(function (r) {
    var _a;
    return ((_a = r._pct) !== null && _a !== void 0 ? _a : 0) > 100;
  });
  var totalDaysOverLimit = totalDays > TOTAL_DAYS_LIMIT;
  var totalHoursOverLimit = totalHours > TOTAL_HOURS_LIMIT;
  var totalPctOverLimit = totalShrinkage > TOTAL_PCT_LIMIT;
  var anyTotalOverLimit = totalDaysOverLimit || totalHoursOverLimit || totalPctOverLimit;
  var handleSave = useCallback(function () {
    if (rows.some(function (r) {
      var _a;
      return ((_a = r._pct) !== null && _a !== void 0 ? _a : 0) > 100;
    })) return;
    if (totalDays > TOTAL_DAYS_LIMIT || totalHours > TOTAL_HOURS_LIMIT || totalShrinkage > TOTAL_PCT_LIMIT) return;
    var updated = rows.map(function (r) {
      return __assign(__assign({}, r), {
        daysPerYear: persistNumeric(r._days),
        hoursPerYear: persistNumeric(r._hours),
        percentShrinkage: persistNumeric(r._pct)
      });
    });
    onSave(updated);
    onClose();
  }, [rows, onSave, onClose, totalDays, totalHours, totalShrinkage]);
  var formatPct = function (pct) {
    return "".concat(formatNumberByLocale(round2(pct), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }), " %");
  };
  var formatDayHourTotal = function (n) {
    return Number.isFinite(n) ? formatNumberByLocale(round2(n), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }) : '0.0';
  };
  var formatMonthWeek = function (hoursPerYear) {
    return {
      mth: formatNumberByLocale(round2(hoursPerMonthFromHoursPerYear(hoursPerYear)), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }),
      wk: formatNumberByLocale(round2(hoursPerWeekFromHoursPerYear(hoursPerYear)), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    };
  };
  var plannedAriaLabel = t('plannedBadge', {
    defaultValue: 'Planned'
  });
  var unplannedAriaLabel = t('unplannedBadge', {
    defaultValue: 'Unplanned'
  });
  var renderSectionHeader = function (sectionTitle) {
    return _jsxs(TableRow, {
      children: [_jsx(HeaderCell, __assign({
        sx: {
          textAlign: 'left'
        }
      }, {
        children: sectionTitle
      })), _jsx(HeaderCell, {
        children: t('daysPerYear', {
          defaultValue: 'Shrinkage days/yr'
        })
      }), _jsx(HeaderCell, {
        children: t('hoursPerYear', {
          defaultValue: 'Shrinkage hrs/yr'
        })
      }), _jsx(HeaderCell, __assign({
        sx: pctColumnBorderSx
      }, {
        children: t('percentShrinkage', {
          defaultValue: 'Shrinkage %'
        })
      })), _jsx(HeaderCell, {
        children: t('hoursPerMonth', {
          defaultValue: 'Total hrs/mth'
        })
      }), _jsx(HeaderCell, {
        children: t('hoursPerWeek', {
          defaultValue: 'Total hrs/week'
        })
      })]
    });
  };
  var renderSection = function (category, sectionRows) {
    var sectionPct = category === SHRINKAGE_CATEGORY_OUT_OF_OFFICE ? oosTotal : iosTotal;
    var sectionHoursAggregate = category === SHRINKAGE_CATEGORY_OUT_OF_OFFICE ? oosHours : iosHours;
    var sectionDaysAggregate = category === SHRINKAGE_CATEGORY_OUT_OF_OFFICE ? oosDays : iosDays;
    var _a = formatMonthWeek(sectionHoursAggregate),
      sectionMth = _a.mth,
      sectionWk = _a.wk;
    var sectionTitle = category === SHRINKAGE_CATEGORY_OUT_OF_OFFICE ? t('outOfOffice', {
      defaultValue: 'Out-of-Office'
    }) : t('inOffice', {
      defaultValue: 'In-Office'
    });
    var totalLabel = category === SHRINKAGE_CATEGORY_OUT_OF_OFFICE ? t('totalOutOfOffice', {
      defaultValue: 'Total out-of-office shrinkage'
    }) : t('totalInOffice', {
      defaultValue: 'Total in-office shrinkage'
    });
    var factorGroups = buildFactorGroups(sectionRows);
    var renderFactorRow = function (row, label, rowSx, isSubcategory) {
      var _a, _b, _c, _d, _e;
      if (isSubcategory === void 0) {
        isSubcategory = false;
      }
      var globalIdx = rows.indexOf(row);
      var hrs = (_a = row._hours) !== null && _a !== void 0 ? _a : 0;
      var _f = formatMonthWeek(hrs),
        mth = _f.mth,
        wk = _f.wk;
      var pctOverLimit = ((_b = row._pct) !== null && _b !== void 0 ? _b : 0) > 100;
      var hasStaffSettings = paidHoursPerDay > 0 && workingDaysPerYear > 0;
      var staffSettingsTooltip = t('staffSettingsRequired', {
        defaultValue: 'Configure Agent Staff Settings to enter days and hours values.'
      });
      var resolvedLabel = isSubcategory ? label : tShrinkage("factors.".concat(SHRINKAGE_FACTOR_I18N_KEYS[label]), {
        defaultValue: label
      });
      return _jsxs(TableRow, __assign({
        sx: rowSx
      }, {
        children: [_jsx(TableCell, __assign({
          sx: __assign(__assign(__assign({}, labelCellSx), rowSx), isSubcategory && {
            pl: TABLE_HIERARCHY_INDENTS[1]
          })
        }, {
          children: _jsx(FactorLabel, {
            row: row,
            label: resolvedLabel,
            plannedAriaLabel: plannedAriaLabel,
            unplannedAriaLabel: unplannedAriaLabel
          })
        })), _jsx(TableCell, __assign({
          sx: __assign(__assign({}, numericCellSx), rowSx)
        }, {
          children: _jsx(Tooltip, __assign({
            title: hasStaffSettings ? '' : staffSettingsTooltip,
            placement: "top"
          }, {
            children: _jsx(Box, __assign({
              sx: {
                width: '100%'
              }
            }, {
              children: _jsx(ShrinkageNumericCellInput, {
                ariaLabel: t('daysPerYear', {
                  defaultValue: 'Shrinkage days/yr'
                }),
                value: (_c = row._days) !== null && _c !== void 0 ? _c : null,
                onValueChange: function (val) {
                  return updateCell(globalIdx, '_days', val);
                },
                max: getFieldMax('_days'),
                disabled: !hasStaffSettings
              })
            }))
          }))
        })), _jsx(TableCell, __assign({
          sx: __assign(__assign({}, numericCellSx), rowSx)
        }, {
          children: _jsx(Tooltip, __assign({
            title: hasStaffSettings ? '' : staffSettingsTooltip,
            placement: "top"
          }, {
            children: _jsx(Box, __assign({
              sx: {
                width: '100%'
              }
            }, {
              children: _jsx(ShrinkageNumericCellInput, {
                ariaLabel: t('hoursPerYear', {
                  defaultValue: 'Shrinkage hrs/yr'
                }),
                value: (_d = row._hours) !== null && _d !== void 0 ? _d : null,
                onValueChange: function (val) {
                  return updateCell(globalIdx, '_hours', val);
                },
                max: getFieldMax('_hours'),
                disabled: !hasStaffSettings
              })
            }))
          }))
        })), _jsx(TableCell, __assign({
          sx: __assign(__assign(__assign(__assign({}, numericCellSx), pctColumnBorderSx), rowSx), pctOverLimit && {
            color: 'error.main',
            fontWeight: 600
          })
        }, {
          children: _jsx(ShrinkageNumericCellInput, {
            ariaLabel: t('percentShrinkage', {
              defaultValue: 'Shrinkage %'
            }),
            value: (_e = row._pct) !== null && _e !== void 0 ? _e : null,
            onValueChange: function (val) {
              return updateCell(globalIdx, '_pct', val);
            },
            max: getFieldMax('_pct')
          })
        })), _jsx(TableCell, __assign({
          sx: __assign(__assign({}, numericCellSx), rowSx)
        }, {
          children: _jsx(Typography, __assign({
            variant: "body2",
            component: "span",
            sx: numericDisplaySx
          }, {
            children: mth
          }))
        })), _jsx(TableCell, __assign({
          sx: __assign(__assign({}, numericCellSx), rowSx)
        }, {
          children: _jsx(Typography, __assign({
            variant: "body2",
            component: "span",
            sx: numericDisplaySx
          }, {
            children: wk
          }))
        }))]
      }), globalIdx);
    };
    var renderFactorParentRow = function (factorName, groupIdx, groupRows) {
      var days = groupRows.reduce(function (s, r) {
        var _a;
        return s + ((_a = r._days) !== null && _a !== void 0 ? _a : 0);
      }, 0);
      var hours = groupRows.reduce(function (s, r) {
        var _a;
        return s + ((_a = r._hours) !== null && _a !== void 0 ? _a : 0);
      }, 0);
      var pct = groupRows.reduce(function (s, r) {
        var _a;
        return s + ((_a = r._pct) !== null && _a !== void 0 ? _a : 0);
      }, 0);
      var _a = formatMonthWeek(hours),
        mth = _a.mth,
        wk = _a.wk;
      var rollupCellSx = __assign(__assign({}, numericCellSx), factorRowSx);
      var rollup = function (value) {
        return _jsx(Typography, __assign({
          variant: "body2",
          component: "span",
          sx: numericDisplaySx
        }, {
          children: value
        }));
      };
      return _jsxs(TableRow, __assign({
        sx: factorRowSx
      }, {
        children: [_jsx(TableCell, __assign({
          sx: __assign(__assign({}, labelCellSx), factorRowSx)
        }, {
          children: _jsx(Typography, __assign({
            variant: "body2"
          }, {
            children: tShrinkage("factors.".concat(SHRINKAGE_FACTOR_I18N_KEYS[factorName]), {
              defaultValue: factorName
            })
          }))
        })), _jsx(TableCell, __assign({
          sx: rollupCellSx
        }, {
          children: rollup(formatDayHourTotal(days))
        })), _jsx(TableCell, __assign({
          sx: rollupCellSx
        }, {
          children: rollup(formatDayHourTotal(hours))
        })), _jsx(TableCell, __assign({
          sx: __assign(__assign({}, rollupCellSx), pctColumnBorderSx)
        }, {
          children: rollup(formatPct(pct))
        })), _jsx(TableCell, __assign({
          sx: rollupCellSx
        }, {
          children: rollup(mth)
        })), _jsx(TableCell, __assign({
          sx: rollupCellSx
        }, {
          children: rollup(wk)
        }))]
      }), "factor-head-".concat(category, "-").concat(groupIdx));
    };
    return _jsxs(_Fragment, {
      children: [renderSectionHeader(sectionTitle), factorGroups.map(function (group, groupIdx) {
        var hasSubcat = function (r) {
          return r.shrinkageSubcategory === ShrinkageSubcategory.Custom ? !!r.customSubcategoryName : !!r.shrinkageSubcategory;
        };
        var rowsWithSubcategory = group.rows.filter(hasSubcat);
        var rowsWithoutSubcategory = group.rows.filter(function (r) {
          return !hasSubcat(r);
        });
        return _jsxs(Fragment, {
          children: [rowsWithoutSubcategory.map(function (row) {
            return renderFactorRow(row, getFactorName(row), factorRowSx);
          }), rowsWithSubcategory.length > 0 && _jsxs(_Fragment, {
            children: [renderFactorParentRow(group.factorName, groupIdx, rowsWithSubcategory), rowsWithSubcategory.map(function (row) {
              var subLabel = getSubcategoryLabel(row, tTerm, SHRINKAGE_SUBCATEGORY_TRANS_KEYS);
              return renderFactorRow(row, subLabel !== null && subLabel !== void 0 ? subLabel : group.factorName, subcategoryRowSx, true);
            })]
          })]
        }, "factor-".concat(category, "-").concat(groupIdx));
      }), sectionRows.length > 0 && _jsxs(TableRow, __assign({
        sx: sectionTotalRowSx
      }, {
        children: [_jsx(TableCell, __assign({
          sx: labelCellSx
        }, {
          children: _jsx(Typography, __assign({
            fontWeight: 600,
            variant: "body2",
            sx: {
              color: 'text.primary'
            }
          }, {
            children: totalLabel
          }))
        })), _jsx(TableCell, __assign({
          sx: numericCellSx
        }, {
          children: _jsx(Typography, __assign({
            variant: "body2",
            sx: numericDisplaySx
          }, {
            children: formatDayHourTotal(sectionDaysAggregate)
          }))
        })), _jsx(TableCell, __assign({
          sx: numericCellSx
        }, {
          children: _jsx(Typography, __assign({
            variant: "body2",
            sx: numericDisplaySx
          }, {
            children: formatDayHourTotal(sectionHoursAggregate)
          }))
        })), _jsx(TableCell, __assign({
          sx: __assign(__assign({}, numericCellSx), pctColumnBorderSx)
        }, {
          children: _jsx(Typography, __assign({
            variant: "body2",
            fontWeight: 600,
            sx: numericDisplaySx
          }, {
            children: formatPct(sectionPct)
          }))
        })), _jsx(TableCell, __assign({
          sx: numericCellSx
        }, {
          children: _jsx(Typography, __assign({
            variant: "body2",
            sx: numericDisplaySx
          }, {
            children: sectionMth
          }))
        })), _jsx(TableCell, __assign({
          sx: numericCellSx
        }, {
          children: _jsx(Typography, __assign({
            variant: "body2",
            sx: numericDisplaySx
          }, {
            children: sectionWk
          }))
        }))]
      }))]
    });
  };
  var showFooter = rows.length > 0;
  var _c = formatMonthWeek(totalHours),
    totalMth = _c.mth,
    totalWk = _c.wk;
  return _jsxs(Dialog, __assign({
    open: open,
    onClose: onClose,
    maxWidth: "lg",
    fullWidth: true
  }, {
    children: [_jsxs(DialogTitle, __assign({
      variant: "h2"
    }, {
      children: [t('title', {
        defaultValue: 'Shrinkage'
      }), ": ", locationName]
    })), _jsxs(StyledDialogContent, {
      children: [_jsx(Typography, __assign({
        sx: {
          mb: 1.5,
          display: 'block'
        }
      }, {
        children: t('instruction', {
          defaultValue: 'Enter the shrinkage amount in either days OR hours across this site location.'
        })
      })), pctOverLimitRows.length > 0 && _jsx(Typography, __assign({
        color: "error",
        variant: "body2",
        sx: {
          mb: 1.5
        }
      }, {
        children: t('percentOverLimit', {
          defaultValue: 'Shrinkage % cannot exceed 100% for any individual factor. Please correct the highlighted values.'
        })
      })), anyTotalOverLimit && _jsx(Typography, __assign({
        color: "error",
        variant: "body2",
        sx: {
          mb: 1.5
        }
      }, {
        children: t('totalOverLimit', {
          defaultValue: 'Total Shrinkage cannot exceed 365 days/year, 2920 hours/year, or 99.99%. Please correct the highlighted values.'
        })
      })), _jsx(TableContainer, __assign({
        component: Paper,
        elevation: 0
      }, {
        children: _jsxs(Table, __assign({
          size: "small",
          sx: tableSx
        }, {
          children: [_jsxs("colgroup", {
            children: [_jsx("col", {
              style: {
                width: '26%'
              }
            }), _jsx("col", {
              style: {
                width: '14%'
              }
            }), _jsx("col", {
              style: {
                width: '14%'
              }
            }), _jsx("col", {
              style: {
                width: '16%'
              }
            }), _jsx("col", {
              style: {
                width: '15%'
              }
            }), _jsx("col", {
              style: {
                width: '15%'
              }
            })]
          }), _jsxs(TableBody, {
            children: [outOfOfficeRows.length > 0 && renderSection(SHRINKAGE_CATEGORY_OUT_OF_OFFICE, outOfOfficeRows), inOfficeRows.length > 0 && renderSection(SHRINKAGE_CATEGORY_IN_OFFICE, inOfficeRows)]
          }), showFooter && _jsx(TableFooter, {
            children: _jsxs(TableRow, __assign({
              sx: finalTotalRowSx
            }, {
              children: [_jsx(TableCell, __assign({
                sx: __assign(__assign({}, labelCellSx), finalTotalRowSx)
              }, {
                children: _jsx(Typography, __assign({
                  fontWeight: "bold",
                  variant: "body2",
                  color: "inherit"
                }, {
                  children: t('totalShrinkage', {
                    defaultValue: 'Total Shrinkage'
                  })
                }))
              })), _jsx(TableCell, __assign({
                sx: __assign(__assign(__assign({}, numericCellSx), finalTotalRowSx), totalDaysOverLimit && {
                  color: 'error.light',
                  fontWeight: 600
                })
              }, {
                children: _jsx(Typography, __assign({
                  fontWeight: "bold",
                  variant: "body2",
                  color: "inherit",
                  sx: numericDisplaySx
                }, {
                  children: formatDayHourTotal(totalDays)
                }))
              })), _jsx(TableCell, __assign({
                sx: __assign(__assign(__assign({}, numericCellSx), finalTotalRowSx), totalHoursOverLimit && {
                  color: 'error.light',
                  fontWeight: 600
                })
              }, {
                children: _jsx(Typography, __assign({
                  fontWeight: "bold",
                  variant: "body2",
                  color: "inherit",
                  sx: numericDisplaySx
                }, {
                  children: formatDayHourTotal(totalHours)
                }))
              })), _jsx(TableCell, __assign({
                sx: __assign(__assign(__assign(__assign({}, numericCellSx), pctColumnBorderSx), finalTotalRowSx), totalPctOverLimit && {
                  color: 'error.light',
                  fontWeight: 600
                })
              }, {
                children: _jsx(Typography, __assign({
                  fontWeight: "bold",
                  variant: "body2",
                  color: "inherit",
                  sx: numericDisplaySx
                }, {
                  children: formatPct(totalShrinkage)
                }))
              })), _jsx(TableCell, __assign({
                sx: __assign(__assign({}, numericCellSx), finalTotalRowSx)
              }, {
                children: _jsx(Typography, __assign({
                  fontWeight: "bold",
                  variant: "body2",
                  color: "inherit",
                  sx: numericDisplaySx
                }, {
                  children: totalMth
                }))
              })), _jsx(TableCell, __assign({
                sx: __assign(__assign({}, numericCellSx), finalTotalRowSx)
              }, {
                children: _jsx(Typography, __assign({
                  fontWeight: "bold",
                  variant: "body2",
                  color: "inherit",
                  sx: numericDisplaySx
                }, {
                  children: totalWk
                }))
              }))]
            }))
          })]
        }))
      }))]
    }), _jsxs(DialogActions, __assign({
      sx: {
        justifyContent: 'space-between'
      }
    }, {
      children: [_jsx(Button, __assign({
        variant: "outlined",
        onClick: onClose
      }, {
        children: tAction('cancel')
      })), _jsx(Button, __assign({
        variant: "contained",
        onClick: handleSave,
        disabled: pctOverLimitRows.length > 0 || anyTotalOverLimit
      }, {
        children: tAction('save')
      }))]
    }))]
  }));
};